import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Breadcrumb } from "~/components/Breadcrumps";
import { Button } from "~/components/Button";
import { SelectInput } from "~/components/Controls/Select";
import { Input } from "~/components/Controls/Input";
import { DetailsView } from "~/components/DetailsView";
import { onSubmitError } from "~/components/Form";
import { useModals } from "~/components/Modal";
import { BreadcrumbsResourcePage } from "~/components/ResourcePage";
import { Title } from "~/components/Title";
import { useAbility } from "~/lib/ability";
import { useDataSource } from "~/lib/dataSource";
import { renderBoolean } from "~/lib/helpers";
import { Intent } from "~/lib/intent";
import { ResourceLoader, useResource, useQueryResource } from "~/lib/loader";
import { notify } from "~/lib/notify";
import { BiddersAbility } from "~/modules/Bidders/ability";
import { fetchBidder, fetchPreTargetingConfigs, prepareBidderSubmitData } from "~/modules/Bidders/api";
import { BidderForm } from "~/modules/Bidders/forms/BidderForm";
import { defaultBidder } from "~/modules/Bidders/models/Bidder";
import { BiddersTable } from "~/modules/Bidders/table/bidders";
import { clientsLinks } from "~/modules/Clients";
import { ClientsAbility } from "~/modules/Clients/ability";
import { agencyBiddersAdapter } from "~/modules/Clients/adapters";
import { fetchAgency, updateAgency, fetchLines, createLine as apiCreateLine, updateLine as apiUpdateLine, deleteLine as apiDeleteLine, ILinesFilter, ILinesPaginator } from "~/modules/Clients/api";
import { AgencyForm } from "~/modules/Clients/forms/AgencyForm";
import { agencyNetworkLabels, isP2pAgency } from "~/modules/Clients/models/Agency";
import { IAgencyBiddersFilter } from "~/modules/Clients/models/filters";
import { LinesTable } from "~/modules/Clients/table/LinesTable";
import { LineForm } from "~/modules/Clients/forms/LineForm";
import { Pagination } from "~/components/Pagination";
import { SecretText } from "~/components/SecretText";
import { useAgencyAccountManagersQuery, useDspConnectionTypesQuery } from "~/modules/Dictionary/queries";
import style from './index.module.scss';

interface IAgencyViewParams {
  agencyId: string;
}

export const AgencyView = () => {
  const params = useParams<IAgencyViewParams>();
  const result = useResource(() => fetchAgency(params.agencyId));
  const accountManagersQuery = useAgencyAccountManagersQuery();
  const accountManagersResult = {
    isPending: accountManagersQuery.isPending,
    resource: accountManagersQuery.data ?? [],
    setResource: (_resource: NonNullable<typeof accountManagersQuery.data>) => undefined,
    fetch: () => {
      void accountManagersQuery.refetch();
    },
  };
  const { data: dspConnectionTypes = [] } = useDspConnectionTypesQuery();
  const modals = useModals();
  const ability = useAbility(ClientsAbility);
  const biddersAbility = useAbility(BiddersAbility);

  const dspLabelByName = useMemo(() => {
    const map = new Map<string, string>();
    dspConnectionTypes.forEach((t) => {
      if (t?.name) {
        map.set(t.name, t.label || t.name);
      }
    });
    return map;
  }, [dspConnectionTypes]);

  const [linesPaginator, setLinesPaginator] = useState<ILinesPaginator>({
    pageNo: 0,
    pageSize: 25,
  });

  const [linesFilter, setLinesFilter] = useState<ILinesFilter>({ excludeDeleted: true });

  const linesResult = useQueryResource(
    () => fetchLines(Number(params.agencyId), linesFilter, linesPaginator),
    [params.agencyId, linesFilter, linesPaginator],
    { items: [], count: 0, page: 0, pageSize: 25 }
  );

  const editAgency = modals.create(AgencyForm, {
    onSubmit: (agency, formikActions, close) => {
      updateAgency(agency)
        .then((updatedAgency) => {
          result.setResource(updatedAgency);
          return updatedAgency;
        })
        .then(close)
        .catch(onSubmitError(formikActions));
    },
  });

  const biddersDataSource = useDataSource(agencyBiddersAdapter);
  const [filter, setFilter] = useState<IAgencyBiddersFilter>({
    agencyId: params.agencyId,
    active: true,
  });
  const rulesConfigIdsCacheRef = useRef<Record<number, number[]>>({});

  const fetchBidders = useCallback(() => {
    biddersDataSource.fetchRecords(filter);
  }, [filter]);

  useEffect(() => {
    fetchBidders();
  }, [fetchBidders]);

  const resolveRulesConfigIds = useCallback(async (bidder) => {
    const bidderId = Number(bidder?.id);
    if (!Number.isFinite(bidderId)) {
      return [];
    }

    const cached = rulesConfigIdsCacheRef.current[bidderId];
    if (Array.isArray(cached)) {
      return cached;
    }

    const configs = await fetchPreTargetingConfigs(bidderId);
    const configIds = Array.from(
      new Set(
        (Array.isArray(configs) ? configs : [])
          .map((config) => Number(config?.id))
          .filter((id) => Number.isFinite(id))
      )
    ).sort((a, b) => b - a);

    rulesConfigIdsCacheRef.current[bidderId] = configIds;
    return configIds;
  }, []);

  const createBidder = modals.create(BidderForm, {
    bidder: { ...defaultBidder, agencyId: Number(params.agencyId) },
    onSubmit: (bidder, formikActions, close) => {
      biddersDataSource
        .createRecord(prepareBidderSubmitData(bidder))
        .then(close)
        .catch(onSubmitError(formikActions));
    },
  });

  const editBidder = modals.create(BidderForm, {
    onSubmit: (bidder, formikActions, close) => {
      biddersDataSource
        .updateRecord(prepareBidderSubmitData({ ...bidder, agencyId: params.agencyId }))
        .then(() => {
          notify.success('Saved');
          close();
        })
        .catch(onSubmitError(formikActions));
    },
  });

  const createLine = modals.create(LineForm, {
    onSubmit: (line, formikActions, close) => {
      apiCreateLine({ ...line, agencyId: Number(params.agencyId) })
        .then(() => {
          linesResult.fetch();
          close();
        })
        .catch(onSubmitError(formikActions));
    },
    agencyId: Number(params.agencyId),
  });

  const editLine = modals.create(LineForm, {
    onSubmit: (line, formikActions, close) => {
      apiUpdateLine({ ...line, agencyId: Number(params.agencyId) })
        .then(() => {
          linesResult.fetch();
          close();
        })
        .catch(onSubmitError(formikActions));
    },
    agencyId: Number(params.agencyId),
  });


  return (
    <ResourceLoader result={result} message="Loading agency">
      {({ resource: agency, isPending }) => (
        <ResourceLoader result={accountManagersResult} message="Loading account managers">
          {({ resource: accountManagers }) => (
            <Breadcrumb breadcrumb={{ to: clientsLinks.agencies(), title: "Agencies" }}>
              <Breadcrumb breadcrumb={{ to: clientsLinks.agency(agency.id), title: agency.title }}>
                <BreadcrumbsResourcePage
                  title="Agency"
                  toolbar={
                    ability.canEditAgency(agency) && (
                      <Button
                        responsive
                        key="edit"
                        disabled={isPending}
                        leftIcon={<i className="fa fa-edit" />}
                        onClick={() => editAgency.show({ agency })}
                      >
                        Edit
                      </Button>
                    )
                  }
                >
                  <DetailsView
                    fields={[
                      { label: "Title", value: agency.title },
                      { label: "Email", value: agency.email },
                      { label: "Site", value: agency.site },
                      { label: "Contact Name", value: agency.contactName },
                      { label: "Account manager", value: accountManagers.find((m) => m.id === agency.accountManagerId)?.name || "N/A" },
                      { label: "Phone", value: agency.phone },
                      { label: "IM", value: agency.instantMessaging },
                      { label: "Active", value: renderBoolean(agency.active) },
                      {
                        label: "External ID",
                        value: agency.externalId ? (
                          <a
                            href={`https://www.appodeal.com/admin/campaign_types/${agency.externalId}`}
                            target="_blank"
                          >
                            {agency.externalId}
                          </a>
                        ) : null,
                      },
                      { label: "Payment type", value: agency.paymentType },
                      { label: "Net Terms", value: agency.netTerms ?? "N/A" },
                      {
                        label: "DSP Connection Type",
                        value: agency.dspConnectionType
                          ? `${dspLabelByName.get(agency.dspConnectionType) ?? agency.dspConnectionType} (${agency.dspConnectionType})`
                          : null,
                      },
                      { label: "DSP Counting Type", value: agency.dspCountingType },
                      { label: "Network", value: agency.network ? agencyNetworkLabels[agency.network] : undefined },
                      {
                        label: "Reporting Timezone",
                        value: agency.reportingTimezone || "UTC",
                      },
                    ]}
                  />
                  <DetailsView
                    title={isP2pAgency(agency.dspConnectionType) ? "P2P" : "DSP"}
                    fields={[
                      ...(isP2pAgency(agency.dspConnectionType)
                        ? [
                            { label: "P2P Coefficient", value: agency.p2pCoefficient },
                            { label: "P2P Gross Bid Price", value: renderBoolean(agency.p2pGrossBidPrice) },
                            { label: "P2P PaaS", value: renderBoolean(agency.attributes?.p2pPaas) },
                            { label: "Is Reverse Payment", value: renderBoolean(agency.attributes?.p2pReversedPayment) },
                            { label: "P2P PaaS Fee %", value: agency.p2pPaasFee },
                          ]
                        : []),
                      { label: "Publisher ID", value: agency.attributes?.publisherId },
                      { label: "Reporting API URL", value: agency.attributes?.reportingApiUrl },
                      { label: "Access Username", value: agency.attributes?.accessUsername },
                      { label: "Access Token", value: <SecretText value={agency.attributes?.accessToken} /> },
                      { label: "Access Password", value: <SecretText value={agency.attributes?.accessPassword} /> },
                    ]}
                  />
                  <div className="layout-content-box">
                    <Title
                      toolbar={
                        <>
                          <SelectInput
                            items={[
                              { label: "All", value: undefined },
                              { label: "Active", value: true },
                              { label: "Inactive", value: false },
                            ]}
                            value={filter.active}
                            disabled={biddersDataSource.state.isPending}
                            onChange={(value) => setFilter({ ...filter, active: value })}
                          />
                          {biddersAbility.canCreate() && (
                            <Button
                              key="create"
                              disabled={biddersDataSource.state.isPending}
                              responsive
                              intent={Intent.PRIMARY}
                              leftIcon={<i className="fa fa-plus" />}
                              onClick={() => createBidder.show()}
                            >
                              Create
                            </Button>
                          )}
                          <Button
                            key="reload"
                            disabled={biddersDataSource.state.isPending}
                            responsive
                            intent={Intent.SUCCESS}
                            leftIcon={<i className="fa fa-refresh" />}
                            onClick={() => biddersDataSource.fetchRecords(filter)}
                          >
                            Reload
                          </Button>
                        </>
                      }
                    >
                      Bidders
                    </Title>
                    <BiddersTable
                      dataSource={biddersDataSource}
                      onEdit={(bidder) => fetchBidder(bidder.id).then(fullBidder => editBidder.show({bidder: fullBidder}))}
                      onMutated={fetchBidders}
                      resolveRulesConfigIds={resolveRulesConfigIds}
                    />
                  </div>

                  <div className="layout-content-box">
                    <Title
                      toolbar={
                        <>
                          <Button
                            key="create-line"
                            disabled={linesResult.isPending}
                            responsive
                            intent={Intent.PRIMARY}
                            leftIcon={<i className="fa fa-plus" />}
                            onClick={() => createLine.show({ dspConnectionType: agency.dspConnectionType })}
                          >
                            Create
                          </Button>
                          <Button
                            key="reload-lines"
                            disabled={linesResult.isPending}
                            responsive
                            intent={Intent.SUCCESS}
                            leftIcon={<i className="fa fa-refresh" />}
                            onClick={() => linesResult.fetch()}
                          >
                            Reload
                          </Button>
                        </>
                      }
                    >
                      Line Management
                    </Title>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
                    <Input
                      placeholder="Filter by Ad System Domain"
                      value={linesFilter.domain || ""}
                      className={style.filterInput}
                      onChange={(value) => {
                        setLinesFilter({ ...linesFilter, domain: value || undefined });
                        setLinesPaginator({ ...linesPaginator, pageNo: 0 });
                        }}
                      rightIcon={<i className='fa fa-search input-search-icon' />}
                      />
                    <Input
                      placeholder="Filter by Seller ID"
                      value={linesFilter.externalSellerId || ""}
                      onChange={(value) => {
                        setLinesFilter({ ...linesFilter, externalSellerId: value || undefined });
                        setLinesPaginator({ ...linesPaginator, pageNo: 0 });
                        }}
                      rightIcon={<i className='fa fa-search input-search-icon' />}
                      />
                    <Input
                      placeholder="Filter by Scope"
                      value={linesFilter.scope || ""}
                      onChange={(value) => {
                        setLinesFilter({ ...linesFilter, scope: value || undefined });
                        setLinesPaginator({ ...linesPaginator, pageNo: 0 });
                        }}
                      rightIcon={<i className='fa fa-search input-search-icon' />}
                      />
                      <SelectInput
                        className={style.filterInput}
                        items={[
                          { label: "All except Deleted", value: "exclude_deleted" },
                          { label: "All Status", value: "all" },
                          { label: "Valid", value: "valid" },
                          { label: "Invalid", value: "invalid" },
                          { label: "Deleted", value: "deleted" },
                          { label: "Pending", value: "pending" },
                          { label: "Manual", value: "manual" },
                        ]}
                        value={linesFilter.excludeDeleted ? "exclude_deleted" : linesFilter.status ?? "all"}
                        onChange={(value) => {
                          const next = value === "exclude_deleted"
                            ? { status: undefined, excludeDeleted: true }
                            : value === "all"
                              ? { status: undefined, excludeDeleted: undefined }
                              : { status: value, excludeDeleted: undefined };
                          setLinesFilter({ ...linesFilter, ...next });
                          setLinesPaginator({ ...linesPaginator, pageNo: 0 });
                        }}
                        placeholder="Select status"
                      />
                    </div>
                    <LinesTable
                      records={linesResult.resource.items || []}
                      isPending={linesResult.isPending}
                      agencyId={Number(params.agencyId)}
                      onEdit={(line) => editLine.show({ line, dspConnectionType: agency.dspConnectionType })}
                      onDelete={(line) => {
                        apiDeleteLine(line).then(() => linesResult.fetch());
                      }}
                      onUpdate={linesResult.fetch}
                    />
                    {!linesResult.isPending && linesResult.resource && (
                      <Pagination
                        padding={4}
                        offset={linesResult.resource.page * linesResult.resource.pageSize}
                        limit={linesResult.resource.pageSize}
                        total={linesResult.resource.count}
                        onChange={(pageNo, perPage) => {
                          setLinesPaginator({ pageNo, pageSize: perPage });
                        }}
                      />
                    )}
                  </div>
                </BreadcrumbsResourcePage>
              </Breadcrumb>
            </Breadcrumb>
          )}
        </ResourceLoader>
      )}
    </ResourceLoader>
  );
};
